/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useRef, useState } from "react";

import cn from "classnames";

import DotIcon from "components/icons/DotIcon";
import HidePasswordIcon from "components/icons/HidePasswordIcon";
import ShowPasswordIcon from "components/icons/ShowPasswordIcon";
import { Input } from "components/ui/Input";

export const checkPassword = (password: string) => ({
  hasMinLength: password?.length >= 12,
  hasOneNumber: /\d/.test(password),
  hasOneUpper: /[A-Z]/.test(password),
  hasOneLower: /[a-z]/.test(password),
  hasSpecialChar: /[^\w\s]/.test(password),
});

const PasswordValidator = ({
  password,
  register,
}: {
  password: string;
  setPassword?: any;
  register?: any;
  hasSpecialChar?: boolean;
}) => {
  const inputRef = useRef(null);

  const passwordCriteria = checkPassword(password);
  const [isPasswordShown, setIsPasswordShown] = useState(false);

  return (
    <div className="w-full text-white">
      <div className="relative">
        <Input
          id="password"
          ref={inputRef}
          type={isPasswordShown ? "text" : "password"}
          className="border-[#535353] mt-1 block w-full outline-none p-4 focus:ring-0 text-base"
          placeholder="**************"
          value={password}
          {...register}
        />

        {/* Show/Hide */}

        <button
          type="button"
          className="absolute flex right-3 items-center bg-secondary-accent h-fit top-1/2 transform -translate-y-1/2 rounded-full"
          onClick={() => setIsPasswordShown(!isPasswordShown)}
        >
          {isPasswordShown ? <HidePasswordIcon /> : <ShowPasswordIcon />}
        </button>
      </div>

      <div className={cn("w-full flex flex-wrap gap-2 text-sm mt-5")}>
        <span
          className={`w-full md:w-[47%] flex items-center gap-1 text-nowrap ${
            passwordCriteria.hasMinLength
              ? "text-[#828282] line-through"
              : "text-gray-300"
          }`}
        >
          <DotIcon
            className={`w-[7px] h-[7px] ${
              passwordCriteria.hasMinLength ? "fill-[#828282]" : "fill-white"
            }`}
          />
          At least 12 Characters
        </span>

        <span
          className={`w-full md:w-[47%] flex items-center gap-1 text-nowrap ${
            passwordCriteria.hasOneLower
              ? "text-[#828282] line-through"
              : "text-gray-300"
          }`}
        >
          <DotIcon
            className={`w-[7px] h-[7px] ${
              passwordCriteria.hasOneLower ? "fill-[#828282]" : "fill-white"
            }`}
          />
          One lower case letter
        </span>

        <span
          className={`w-full md:w-[47%] flex items-center gap-1 text-nowrap ${
            passwordCriteria.hasOneUpper
              ? "text-[#828282] line-through"
              : "text-gray-300"
          }`}
        >
          <DotIcon
            className={`w-[7px] h-[7px] ${
              passwordCriteria.hasOneUpper ? "fill-[#828282]" : "fill-white"
            }`}
          />
          One upper case letter
        </span>

        <span
          className={`w-full md:w-[47%] flex items-center gap-1 text-nowrap ${
            passwordCriteria.hasOneNumber
              ? "text-[#828282] line-through"
              : "text-gray-300"
          }`}
        >
          <DotIcon
            className={`w-[7px] h-[7px] ${
              passwordCriteria.hasOneNumber ? "fill-[#828282]" : "fill-white"
            }`}
          />
          One number
        </span>

        <span
          className={`w-full flex items-center gap-1 text-nowrap ${
            passwordCriteria.hasSpecialChar
              ? "text-[#828282] line-through"
              : "text-gray-300"
          }`}
        >
          <DotIcon
            className={`w-[7px] h-[7px] ${
              passwordCriteria.hasSpecialChar ? "fill-[#828282]" : "fill-white"
            }`}
          />
          One special character (e.g., !, @, #, $, %, etc.)
        </span>
      </div>
    </div>
  );
};

export default PasswordValidator;
